import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, Renderer2 } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { combineLatest, debounceTime, map, Subject, Subscription, take, takeUntil, tap } from 'rxjs';
import { differenceInDays, endOfDay, isSameDay, startOfDay } from 'date-fns';
import { toPng } from 'html-to-image';

import { ANIMATION_SLIDE_LEFT_COMPONENT } from '@src/app/utils/animation-components';
import { IFilterVehicle, IFilterVehicleOption, ISubmitFilters } from '@src/app/interfaces/filters-vehicle.interface';
import { FilterControlComponent } from '@shared/components/vehicle-list/filter-control/filter-control.component';
import { DatePickerComponent } from '@src/app/standalone-components/date-picker/date-picker.component';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { FilterVehicleType } from '@src/app/enum/filters-vehicle.enum';
import { Destroyable } from '@src/app/utils/Destroyable';
import { Vehicle } from '@commons/models/domain/Vehicle';
import { MapService } from '@commons/services/map/map/map.service';
import { ReportDayRoutePreviewComponent } from './day-route-preview/report-day-route-preview.component';
import { RouteDetailsModalComponent } from './route-details-modal/route-details-modal.component';
import { RouteReportsSkeletonLoaderComponent } from './route-reports-skeleton-loader/route-reports-skeleton-loader.component';
import { RouteService } from '@commons/services/route/route.service';
import { GroupRoutesByDatePipe } from '@shared/pipes/group-routes-by-date.pipe';
import { RouteStats } from '@commons/models/domain/RouteStats';
import { RouteSummaryStats } from '@src/app/interfaces/route-stats.interface';
import { calculateFullStopTimeRoutes, CalculateRouteSummaryParamsByStats, calculateStopTimeRoute } from '@src/app/utils/routes';
import { PipesModule } from '@commons/pipes/pipes.module';
import { formatDate, formatMinutesToHours, sortRoutesByDate } from '@src/app/utils/date';
import { SummaryRouteComponent } from './summary-route/summary-route.component';
import { RouteDrawerService } from '@commons/services/map/drawer/route/route-drawer.service';
import { NewVehiclesHttpService } from '@shared/services-http/new-vehicles-http.service';
import { RouteElementTypeEnum } from '@commons/enums/route-element-type.enum';
import { IGroupObjRouteEventReport, IRouteEventReportData } from '@src/app/interfaces/report-route.interface';
import { RouteEvent } from '@commons/models/domain/RouteEvent';
import { EventService } from '@commons/services/event/event.service';
import { EventTypeEnum } from '@commons/enums/event-type.enum';
import { BusinessEvent } from '@commons/models/domain/BusinessEvent';
import { GenerateXlsFileReport, ReportRowBuilder } from '@src/app/utils/reports/xls-report';
import { NewDataStoreService } from '@shared/services/new-data-store.service';
import { PdfGenerator } from '@src/app/utils/pdf-generator/pdf-generator';
import { IPdfDataObject } from '@src/app/interfaces/pdf.interface';

interface GetRoutesSubscriptions {
  getRouteForDateSubsc: Subscription,
  showPointInMapSubsc?: Subscription,
  unsubscribeAll: Function;
}

enum SortMode {
  Latest = 1,
  Oldest = 2,
}

@Component({
  selector: 'app-route-reports',
  standalone: true,
  imports: [CommonModule, TranslateModule, FilterControlComponent, SummaryRouteComponent, PipesModule, DatePickerComponent, RouteReportsSkeletonLoaderComponent, FormsModule, ButtonModule, ReportDayRoutePreviewComponent, RouteDetailsModalComponent],
  templateUrl: './route-reports.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./route-reports.component.scss'],
  animations: ANIMATION_SLIDE_LEFT_COMPONENT,
  providers: [GroupRoutesByDatePipe]
})
export class RouteReportsComponent extends Destroyable implements OnInit {

  @Input('isVisible') set isVisible(value: boolean) {
    this._isVisible = value;

    if (value) {
      this.setFiltersConfig();
      return;
    }
    this.resetState();
    this.destroy();
  }

  public _isVisible = true;
  public isInitialStateEmpty = true;
  public isLoadingReport: boolean;
  public isVisibleRouteDetailsModal: boolean;
  public isGeneretingRaport: boolean;
  public statusVisibleGroups: Map<number, boolean> = new Map([]);

  public sortMode: SortMode = SortMode.Latest;
  public vehicleRouteStatisticSummary: RouteSummaryStats & { stopTime: string };
  public selectedRoute: IRouteEventReportData;
  public selectedGroupIndex: number;
  public selectedRouteIndexInGroup: number;

  // filtry
  public filtersVehicleConfig: IFilterVehicle;
  public filtersVehicleCurrentFilters: ISubmitFilters;
  public selectedDate: Date[];
  public selectedVehicle: Vehicle;

  //
  protected groupRoutes: IGroupObjRouteEventReport[] = [];
  protected routeDataWithPoints: Map<number, IRouteEventReportData> = new Map([]);
  public readonly onGenerateRoutesInMap$: Subject<void> = new Subject();
  public readonly SortMode = SortMode;

  constructor(
    private vehicleService: VehicleService,
    private translate: TranslateService,
    private changeDetRef: ChangeDetectorRef,
    private renderer2: Renderer2,
    private mapService: MapService,
    private routeService: RouteService,
    private groupRoutesByDatePipe: GroupRoutesByDatePipe,
    private routeDrawer: RouteDrawerService,
    private newVehiclesHttpService: NewVehiclesHttpService,
    private eventService: EventService,
    private newDataStoreService: NewDataStoreService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.onGenerateRoute();
  }

  private get selectedDateFormated(): string {
    const [startDate, endDate] = this.selectedDate;
    return `${formatDate('dd.MM.yyyy', startDate)}${ endDate ? ('-' + formatDate('dd.MM.yyyy', endDate)) : '' }`;
  }

  public generateXlsFile(): void {
    const routes = this.groupRoutes
      .reduce((prev: IRouteEventReportData[], curr: IGroupObjRouteEventReport) => prev.concat(curr.routes), [])
      .filter(item => item.T === RouteElementTypeEnum.Segment);

    const fileName = `Raport z tras ${ this.selectedVehicle.Nm } ${ this.selectedDateFormated }`;
    const generateXlsFileReport = new GenerateXlsFileReport(routes, new ReportRowBuilder(this.newDataStoreService));
    generateXlsFileReport.generateReport(fileName);
  }

  public async generatePDFFile(): Promise<void> {
    const mapBase64 = await toPng(document.getElementsByClassName('leaflet-container').item(0) as HTMLElement, { skipFonts: true });

    const routes = this.groupRoutes
      .reduce((prev: IRouteEventReportData[], curr: IGroupObjRouteEventReport) => prev.concat(curr.routes), [])
      .filter(item => item.T === RouteElementTypeEnum.Segment);

    const fileName = `Raport z tras ${ this.selectedVehicle.Nm } ${ this.selectedDateFormated }.pdf`;

    const reportRowBuilder = new ReportRowBuilder(this.newDataStoreService);

    const headerRouteTable = ['Czas od', 'Czas do', 'Start', 'Koniec', 'Dystans (km)', 'Czas jazdy', 'Czas postoju', 'Kierowca'];
    const dataRouteTable = routes.map(entry => reportRowBuilder.buildRow(entry));

    const pdfElements: IPdfDataObject[] = [
      { index: 0, text: `Pojazd: ${ this.selectedVehicle.Nm }`, isHeaderNewPage: true },
      { index: 1, text: `Dane raportu z: ${ this.selectedDateFormated }`, isHeaderNewPage: true },
      { index: 4, text: `Podsumowanie wybranego zakresu:` },
      {
        index: 5,
        table: {
          headers: [['Dystans (km)', 'Czas jazdy', 'Czas postoju', 'Zuzyte paliwo', 'Zuzycie L/100km']],
          data: [
            [
              this.vehicleRouteStatisticSummary.distance.toFixed(2),
              this.vehicleRouteStatisticSummary.time.toString(),
              this.vehicleRouteStatisticSummary.stopTime,
              this.vehicleRouteStatisticSummary.fuel.toFixed(2),
              `${this.vehicleRouteStatisticSummary.fuelPer100.toFixed(2)}L/100km`
            ] 
          ]
        }
      },
      { index: 6, space: { height: 20, width: 40 } },
      { index: 7, text: `Dane tras z wybranego zakresu:` },
      {
        index: 8,
        table: {
          headers: [headerRouteTable],
          data: dataRouteTable.map(row => {
            const keys = Object.keys(row);
            // @ts-ignore
            return keys.map((el) => row[el]);
          }),
        }
      },
      { index: 10, text: `Trasy z wybranego okresu:`, newPage: true },
      { index: 11, image: mapBase64 },
    ];

    const generator = new PdfGenerator(pdfElements, {
      spacing: 5,
      topMargin: 20,
      sideMargin: 20,
      fileName,
    });
    generator.generatePdf();
  }

  public trackByGroupRoutes(_: number, item: IGroupObjRouteEventReport): number {
    return item.id;
  }

  public showAllRoutesOnMap(): void {
    this.unSelectRouteDay();
    this.statusVisibleGroups.forEach((_, key) => this.statusVisibleGroups.set(key, true));
    this.refreshRoutesOnMap();
  }

  public toggleVisibilityGroup(group: IGroupObjRouteEventReport, indexGroup: number): void {
    const currentState = this.statusVisibleGroups.get(group.id) || false;
    const newState = !currentState;
    this.statusVisibleGroups.set(group.id, newState);
    this.refreshRoutesOnMap();
  }

  private refreshRoutesOnMap(): void {
    this.mapService.setMapLoaderState(true);
    this.routeDrawer.setRoute(this.getRouteWithPointsFromVisibleGroups(), true);
    setTimeout(() => this.mapService.setMapLoaderState(false), 300);
  }

  private onGenerateRoute(): void {
    let getRoutesSubscriptions: GetRoutesSubscriptions;
    this.onGenerateRoutesInMap$
    .pipe(
      takeUntil(this.destroy$),
      tap(() => this.setStateLoadingNewRoute(getRoutesSubscriptions)),
      debounceTime(500),
    )
    .subscribe({
      next: () => getRoutesSubscriptions = this.getRoutesForDate(),
    });
  }

  private setStateLoadingNewRoute(data: GetRoutesSubscriptions): void {
    data?.unsubscribeAll();
    this.routeDrawer.setRoute(undefined);
    this.mapService.setMapLoaderState(true);
    this.isLoadingReport = true;
    this.changeDetRef.markForCheck();
  }

  public onSelectRoute({ indexRouteInGroup, route }: any, indexGroup: number): void {
    if (route.Id !== this.selectedRoute?.Id) {
      this.selectedGroupIndex = indexGroup;
      this.selectedRouteIndexInGroup = indexRouteInGroup;
      this.selectRouteDay(route);
      return;
    }
    this.unSelectRouteDay();
  }

  private onClickPrevNextBtnDetailsModal(
    lastRouteInPrevGroupIndex: number,
    newRouteSelectedData: IRouteEventReportData,
    groupIndex: number,
    groupId: number,
  ): void {
    const statusVisibleGroup = this.statusVisibleGroups.get(groupId);
    this.onSelectRoute({ indexRouteInGroup: lastRouteInPrevGroupIndex, route: newRouteSelectedData }, groupIndex);
    this.scrollToSelectedGroup(groupId);
    if (!statusVisibleGroup) this.refreshRoutesOnMap();
  }

  public selectPrevRoute(): void {
    const prevGroupIndex = this.selectedGroupIndex - 1;
    if (this.selectedRouteIndexInGroup === 0 && this.selectedGroupIndex === 0) {
      return;
    }
    const prevGroupData = this.groupRoutes[prevGroupIndex];
    if (this.selectedRouteIndexInGroup === 0) {
      const newGroupRoutes = this.getOnlyRoutesSegmentFromGroup(prevGroupData);
      const lastRouteInPrevGroupIndex = newGroupRoutes.length - 1;
      const newRouteSelectedData = newGroupRoutes[lastRouteInPrevGroupIndex];
      this.onClickPrevNextBtnDetailsModal(lastRouteInPrevGroupIndex, newRouteSelectedData, prevGroupIndex, prevGroupData.id);
      return;
    }
    const currentGroupData = this.getOnlyRoutesSegmentFromGroup(this.groupRoutes[this.selectedGroupIndex]);
    const newRouteSelectedData = currentGroupData[this.selectedRouteIndexInGroup - 1];
    this.onClickPrevNextBtnDetailsModal(this.selectedRouteIndexInGroup-1, newRouteSelectedData, this.selectedGroupIndex, this.groupRoutes[this.selectedGroupIndex].id);
  }

  public selectNextRoute(): void {
    const currentGroupIndex = this.selectedGroupIndex;
    const lengthGroups = this.groupRoutes.length - 1;
    const currentGroupRoutes = this.getOnlyRoutesSegmentFromGroup(this.groupRoutes[currentGroupIndex]);
    const currentRouteIndex = this.selectedRouteIndexInGroup;
    const isLastGroupActive = lengthGroups === currentGroupIndex;

    const isLastRouteGroup = (currentGroupRoutes.length - 1) === currentRouteIndex;
    if (isLastGroupActive && isLastRouteGroup) {
      return;
    }
    if (isLastRouteGroup) {
      const newGroupIndex = currentGroupIndex + 1;
      const newRoute = this.getOnlyRoutesSegmentFromGroup(this.groupRoutes[newGroupIndex])[0];
      this.onClickPrevNextBtnDetailsModal(0, newRoute, newGroupIndex, this.groupRoutes[newGroupIndex].id);
      return;
    }
    const newRoute = currentGroupRoutes[currentRouteIndex + 1];
    this.onClickPrevNextBtnDetailsModal(currentRouteIndex + 1, newRoute, currentGroupIndex, this.groupRoutes[currentGroupIndex].id);
  }

  private scrollToSelectedGroup(idGroup: number): void {
    this.setVisibleGroup(idGroup);
    document.getElementById(`group-id-${idGroup}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private getOnlyRoutesSegmentFromGroup(group: IGroupObjRouteEventReport): IRouteEventReportData[] {
    return group.routes.filter(item => item.T === RouteElementTypeEnum.Segment);
  }

  private getRoutesIdFromGroup(id: number): number[] {
    const selectedGroup = this.groupRoutes.find(el => el.id === id)!;
    return selectedGroup?.routes.map(item => item.Id) || [];
  }

  private getRouteWithPointsFromVisibleGroups(): IRouteEventReportData[] {
    let visibleGroupsIds: number[] = [];
    this.statusVisibleGroups.forEach((isVisible, groupId) => isVisible && visibleGroupsIds.push(groupId));

    const visibleRoutes: IRouteEventReportData[] = visibleGroupsIds.reduce(
      (prev: IRouteEventReportData[], curr: number) => 
        [
          ...prev,
          ...this.getRoutesIdFromGroup(curr).map(routeId => this.routeDataWithPoints.get(routeId)!)
        ],
      []
    );    
    return visibleRoutes;
  }

  private selectRouteDay(data: IRouteEventReportData): void {
    this.selectedRoute = this.routeDataWithPoints.get(data.Id)!;
    this.isVisibleRouteDetailsModal = true;

    setTimeout(() => {
      this.fitMapHeightSize();
      this.routeDrawer.onClickPartOfRoute(this.selectedRoute);
    }, 260);
  }

  private unSelectRouteDay(): void {
    this.routeDrawer.onClickPartOfRoute(undefined);
    this.hideDetailsModal();
  }

  private hideDetailsModal(): void {
    this.selectedRoute = null!;
    this.selectedGroupIndex = null!;
    this.selectedRouteIndexInGroup = null!;
    this.isVisibleRouteDetailsModal = false;
    this.resetMapHeightSize();
  }

  public changeSortDays(mode: SortMode): void {
    this.sortMode = mode;
    const groups = this.groupRoutes.map(el => ({...el, routes: el.routes.reverse()}));
    this.groupRoutes = groups.reverse();
    this.setVisibleGroup(this.groupRoutes[0].id);
    this.unSelectRouteDay();
    this.changeDetRef.detectChanges();
  }

  // TODO: Trzeba zrobić globalne rozwiązanie, które usunie moduły całkowicie, a nie tylko ukrywa, przez to musimy robić takie metody, do resetowania stanu
  private resetState(): void {
    this.isInitialStateEmpty = true;
    this.isGeneretingRaport = false;
    this.isVisibleRouteDetailsModal = false;
    this.isLoadingReport = false;
    this.filtersVehicleCurrentFilters = null!;
    this.selectedDate = null!;
    this.selectedVehicle = null!;
    this.groupRoutes = [];
    this.routeDataWithPoints = new Map([]);
    this.vehicleService.setFollowVehicleStatus(true);
    this.routeDrawer.resetRoute();
    this.setDefaultMapSize();
  }

  public submitFilters(): void {
    this.isInitialStateEmpty = false;
    this.hideDetailsModal();
    this.fitMapWidthSize();
    const ANIMATION_TRANSITION = 200;
    setTimeout(() => this.getData(), ANIMATION_TRANSITION);
  }

  private getData(): void {
    this.onGenerateRoutesInMap$.next();
  }

  private prepareDataToRequestBody() {
    const [from, to] = this.selectedDate;
    const { Id } = this.selectedVehicle;
    return {
      Id,
      from,
      to,
    }
  }

  private setInitialVisibilityGroups(groups: IGroupObjRouteEventReport[]): void {
    groups.forEach((item) => this.statusVisibleGroups.set(item.id, true));
  }

  private setVisibleGroup(id: number): void {
    this.statusVisibleGroups.set(id, true);
  }

  private calculateStopTimeRoutes(routes: RouteEvent[]): IRouteEventReportData[] {
    const routesExtend = routes.map((el, index) => ({
      ...el,
      stopTimeInMinutes: calculateStopTimeRoute(el, routes, index),
    }));
    return routesExtend;
  }

  private getRoutesForDate({ Id, from, to } = this.prepareDataToRequestBody()): GetRoutesSubscriptions {
    let loadPointsForRouteSubsc: Subscription;
    const DateFrom = startOfDay(from);
    const DateTo = endOfDay(to || from);

    const requests = combineLatest([
        this.routeService.getRouteForDate$(DateFrom, DateTo, Id, true).pipe(map((routes) => this.calculateStopTimeRoutes(routes))),
        this.routeService.getRouteStats$(Id, DateFrom, DateTo),
        this.eventService.findEvent({ EventType: EventTypeEnum.Fuel, DateFrom, DateTo, VehicleId: this.selectedVehicle.Id }).pipe(map(res => res.events)),
    ]);
    const ifSelectedMoreThen2DaysRange = differenceInDays(DateTo, DateFrom) > 1 ? true : false;

    const sub = requests.pipe(takeUntil(this.destroy$)).subscribe(
      ([routeData, routeStats, fuelEvents]) => {
        if (!routeData?.length) {
          this.mapService.setMapLoaderState(false);
        }
        else {
          loadPointsForRouteSubsc = this.loadPointsForRoute(routeData, ifSelectedMoreThen2DaysRange);
        }
        this.showReportRoutes(routeData, routeStats, fuelEvents);
      }
    );

    return {
        getRouteForDateSubsc: sub,
        showPointInMapSubsc: loadPointsForRouteSubsc!,
        unsubscribeAll: () => {
          sub?.unsubscribe();
          loadPointsForRouteSubsc?.unsubscribe();
        },
    };
  }

  private showReportRoutes(routeData: IRouteEventReportData[], routeStats: RouteStats, fuelEvents: BusinessEvent[]): void {
    this.setGroupsRoute(routeData, fuelEvents);
    this.setInitialVisibilityGroups(this.groupRoutes);
    this.setVehicleRouteSummary(routeData, routeStats);
    this.isLoadingReport = false;
    this.isGeneretingRaport = true;
    this.changeDetRef.markForCheck();
  }

  private setGroupsRoute(routeData: IRouteEventReportData[], fuelEvents: BusinessEvent[]): void {
    const groups = this.groupRoutesByDatePipe.transform(routeData);
    groups.forEach(el => {
      el.routes = sortRoutesByDate(el.routes, 'St');
      el.events = fuelEvents.filter(item => isSameDay(new Date(el.date), new Date(item.TS)));
    });
    this.groupRoutes = [...groups];
  }

  private setVehicleRouteSummary(routeData: IRouteEventReportData[], stats: RouteStats): void {
    const stopTime = calculateFullStopTimeRoutes(routeData);
    const vehicleStats = CalculateRouteSummaryParamsByStats(stats);
    this.vehicleRouteStatisticSummary = {
      ...vehicleStats,
      stopTime,
      time: formatMinutesToHours(vehicleStats.time) as any,
    };
  }

  private loadPointsForRoute(routes: IRouteEventReportData[], ifSelectedMoreThen2DaysRange?: boolean): Subscription {
    const ids = routes.map(x => x.Id);
    const sub = this.newVehiclesHttpService.getRouteDetailsList(ids!)
    .pipe(
      takeUntil(this.destroy$),
      debounceTime(3000),
    )
    .subscribe({
      next: (response) => {
        response.forEach(responseRoute => {
          const matchedRoute = routes.find(x => x.Id === responseRoute.Id)!;
          matchedRoute.Points = responseRoute.P;
          this.routeDataWithPoints.set(responseRoute.Id, matchedRoute);
        });
        this.routeDrawer.setRoute(routes, ifSelectedMoreThen2DaysRange);
        this.mapService.setMapLoaderState(false);
      }
    });
    return sub;
  }

  // filters
  public onChangeVehicleFilters(data: ISubmitFilters, filterData: IFilterVehicle): void {
    if (data.selectedControlValue) {
      this.getVehicleDataById(data.selectedControlValue);
    }
    this.filtersVehicleCurrentFilters = data.selectedControlValue ? {...data} : (null as any);
  }

  private getVehicleDataById(idVehicle: number): void {
    this.vehicleService.getDetails$(idVehicle).pipe(take(1)).subscribe({
      next: (data) => {
        this.selectedVehicle = data;
        this.hideDetailsModal();
      }
    });
  }

  private setFiltersConfig(): void {
    this.filtersVehicleConfig = {
      filterLabel: this.translate.instant('labels.vehicle'),
      type: FilterVehicleType.Group,
      dropdownLabel: this.translate.instant('chartParams.availableCars'),
      options: [],
      isVisibleSelectedOptionLabel: true,
    };
    this.changeDetRef.markForCheck();
    this.getVehiclesList();
  }

  private getVehiclesList(): void {
    this.vehicleService.getListFiltered$()
    .pipe(
      takeUntil(this.destroy$),
      take(1),
    )
    .subscribe({
      next: (data) => this.setVehicleList(data),
    });
  }

  private setVehicleList(data: Vehicle[]): void {
    this.filtersVehicleConfig = {
      ...this.filtersVehicleConfig,
      options: data.map(item => ({ label: item.Nm, value: item.Id, data: item })) as IFilterVehicleOption[],
    }
  }

  // Map size change
  private get mapWrapperRef(): HTMLElement {
    return document.getElementById('base-map-content') as HTMLElement;
  }

  private get mapContentRef(): Element {
    return document.getElementsByClassName('leaflet-container').item(0) as Element;
  }

  private fitMapWidthSize(): void {
    const newMapWidth = window.innerWidth - 600 - 56;
    const newLeftGap = 56 + 600;
    this.renderer2.setStyle(this.mapContentRef, 'width', `${ newMapWidth }px`);
    this.renderer2.setStyle(this.mapWrapperRef, 'left', `${ newLeftGap }px`);
    this.mapService.map?.invalidateSize({ animate: true });
  }

  private setDefaultMapSize(): void {
    this.renderer2.removeStyle(this.mapContentRef, 'width');
    this.renderer2.setStyle(this.mapContentRef, 'height', '100svh');
    this.renderer2.setStyle(this.mapWrapperRef, 'left', '56px');
    this.mapService.map?.invalidateSize({ animate: true });
  }

  private resetMapHeightSize(): void {
    this.renderer2.setStyle(this.mapContentRef, 'height', '100svh');
    this.mapService.map?.invalidateSize({ animate: true });
  }

  private fitMapHeightSize(): void {
    const HEIGHT_ROUTE_MODAL_DETAILS = 223;
    const newMapHeight = window.innerHeight - HEIGHT_ROUTE_MODAL_DETAILS;
    this.renderer2.setStyle(this.mapContentRef, 'height', `${ newMapHeight }px`);
    this.mapService.map?.invalidateSize({ animate: true });
  }

}
